import { Box, ButtonBase, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined';
import ApartmentIcon from '@mui/icons-material/ApartmentOutlined';
import type { BlockGroup, UnitRow } from '../../lib/unit-directory';
import { chartColors } from '../../theme';
import { useT } from '../../i18n';

// The UI only tells lived-in from vacant; OWNER_ABSENT (rented out) shows as occupied.
const OCCUPANCY_KEY = { OCCUPIED: 'units.occupiedChip', VACANT: 'units.vacantChip', OWNER_ABSENT: 'units.occupiedChip' } as const;

/**
 * How a tile is drawn: solid blue = lived in with someone in the app, pale
 * blue = lived in but nobody registered yet, dashed = vacant. OWNER_ABSENT
 * (rented out) counts as lived in.
 */
type TileKind = 'registered' | 'unregistered' | 'vacant';

function tileKind(row: UnitRow): TileKind {
  if (row.unit.occupancy === 'VACANT') return 'vacant';
  return row.residents.length > 0 ? 'registered' : 'unregistered';
}

function useTileStyles() {
  const theme = useTheme();
  const blue = chartColors(theme.palette.mode).primary;
  return {
    registered: { bgcolor: blue, color: '#fff', border: `1.5px solid ${blue}` },
    unregistered: { bgcolor: alpha(blue, 0.14), color: theme.palette.text.primary, border: `1.5px solid ${blue}` },
    vacant: { bgcolor: 'transparent', color: theme.palette.text.secondary, border: `1.5px dashed ${alpha(theme.palette.text.secondary, 0.55)}` },
  } satisfies Record<TileKind, object>;
}

export function BlockMapLegend() {
  const t = useT();
  const styles = useTileStyles();
  const items: Array<[TileKind, string, object]> = [
    ['registered', t('units.legendRegistered'), styles.registered],
    ['unregistered', t('units.legendUnregistered'), styles.unregistered],
    ['vacant', t('units.vacantChip'), styles.vacant],
  ];
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      {items.map(([kind, label, swatch]) => {
        const item = (
          <Stack key={kind} direction="row" spacing={0.75} alignItems="center" sx={kind === 'unregistered' ? { cursor: 'help' } : undefined}>
            <Box sx={{ width: 14, height: 14, borderRadius: '4px', boxSizing: 'border-box', ...swatch }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Stack>
        );
        // The short label, with what it means on hover.
        return kind === 'unregistered' ? <Tooltip key={kind} title={t('units.unregisteredHelp')} arrow>{item}</Tooltip> : item;
      })}
    </Stack>
  );
}

/** One card per block with a tile per unit. Clicking a tile opens that unit. */
export function BlockMap({
  groups,
  selectedId,
  onSelect,
}: {
  groups: BlockGroup[];
  selectedId: string | null;
  onSelect: (row: UnitRow) => void;
}) {
  const t = useT();
  const theme = useTheme();
  const styles = useTileStyles();

  const tip = (row: UnitRow) => {
    const base = `${row.unit.label} · ${t(OCCUPANCY_KEY[row.unit.occupancy])}`;
    if (row.residents.length === 0) {
      // Same words as the legend; "not registered" only matters for a house someone lives in.
      return row.unit.occupancy !== 'VACANT' ? `${base} · ${t('units.legendUnregistered')}` : base;
    }
    const first = row.residents[0]!.full_name;
    return `${base} · ${row.residents.length === 1 ? first : `${first} +${row.residents.length - 1}`}`;
  };

  return (
    <Stack spacing={2}>
      {groups.map((g) => (
        <Card key={g.block} variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={600}>{g.block || '—'}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('units.blockStats', { units: g.units, occupied: g.occupied, residents: g.residents })}
              </Typography>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))', gap: 1 }}>
              {g.rows.map((row) => {
                const selected = row.unit.id === selectedId;
                const TypeIcon = row.unit.type === 'RUKO' ? StorefrontIcon : row.unit.type === 'APARTMENT' ? ApartmentIcon : null;
                return (
                  <Tooltip key={row.unit.id} title={tip(row)} arrow disableInteractive>
                    <ButtonBase
                      onClick={() => onSelect(row)}
                      aria-label={tip(row)}
                      sx={{
                        position: 'relative',
                        height: 48,
                        borderRadius: '10px',
                        boxSizing: 'border-box',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        fontVariantNumeric: 'tabular-nums',
                        transition: 'transform 120ms ease, box-shadow 120ms ease',
                        ...styles[tileKind(row)],
                        '&:hover, &.Mui-focusVisible': { transform: 'translateY(-2px)', boxShadow: 2 },
                        ...(selected ? { outline: `2px solid ${theme.palette.text.primary}`, outlineOffset: 2 } : {}),
                      }}
                    >
                      {row.unit.number}
                      {TypeIcon ? (
                        <TypeIcon sx={{ position: 'absolute', top: 3, right: 3, fontSize: 12, opacity: 0.85 }} />
                      ) : null}
                    </ButtonBase>
                  </Tooltip>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
